export interface AuthMe {
  user: { id: number; username: string };
  csrfToken: string;
  features?: { runpodImages?: boolean; runpodVolume?: boolean };
}

export interface ApplicationCapabilities {
  runpod: boolean;
  identityAdmin: boolean;
}

export interface ApplicationProxyConfig {
  composeProject: string;
  composeService: string;
  internalPort: number;
  metricsTokenRef: string | null;
  network: string | null;
}

export interface Application {
  id: number;
  slug: string;
  name: string;
  isBuiltin: boolean;
  capabilities: ApplicationCapabilities;
  /** True when the app declares proxy-replica monitoring. */
  proxyMonitoring: boolean;
  /** True when the app's GitHub token env var is set (else Branches/Deploy fail). */
  githubConfigured: boolean;
  github: { owner: string; repo: string; protectedBranch: string; tokenRef: string };
  deploy: { projectDir: string; branch: string; timeoutSeconds: number | null };
  proxy: ApplicationProxyConfig | null;
}

export interface ApplicationsResponse {
  applications: Application[];
}

/** Request body for creating / updating an application. */
export interface ApplicationUpsert {
  name: string;
  slug?: string;
  github: { owner: string; repo: string; tokenRef: string; protectedBranch?: string };
  deploy: { projectDir: string; branch?: string; timeoutSeconds?: number | null };
  proxy?: ApplicationProxyConfig | null;
}

export interface BranchSummary {
  name: string;
  sha: string;
  lastCommitAt: string;
  lastCommitMessage: string;
  lastAuthor: string | null;
  isProtected: boolean;
}

export interface BranchesResponse {
  branches: BranchSummary[];
  protectedBranch: string;
}

export type BranchCompareStatus = 'identical' | 'ahead' | 'behind' | 'diverged';

export interface BranchComparison {
  name: string;
  /** Commits this branch has that the reference does not. */
  ahead: number;
  /** Commits the reference has that this branch does not. */
  behind: number;
  status: BranchCompareStatus | null;
  error: string | null;
}

export interface BranchComparisonResponse {
  base: string;
  comparisons: BranchComparison[];
}

export interface DeploymentRecord {
  id: number;
  userId: number;
  username: string;
  /** Name of the API key that triggered the deploy, if any. */
  apiKeyName: string | null;
  branch: string;
  commitSha: string | null;
  status: 'running' | 'success' | 'failed' | 'timeout';
  startedAt: number;
  finishedAt: number | null;
  exitCode: number | null;
  logFile: string;
  errorMessage: string | null;
}

export interface DeleteBranchesResult {
  results: { branch: string; ok: boolean; error: string | null }[];
}

export interface MergeResult {
  status: 'merged' | 'already_merged';
  sha: string | null;
  message: string;
}

export interface HostMetrics {
  hostname: string;
  kernel: string;
  uptimeSeconds: number;
  loadAvg: [number, number, number];
  cpu: { cores: number; usage: number };
  memory: {
    total: number;
    available: number;
    used: number;
    free: number;
    buffers: number;
    cached: number;
    swapTotal: number;
    swapUsed: number;
  };
  disk: { path: string; total: number; free: number; used: number };
  net: { rxBytes: number; txBytes: number; rxRate: number; txRate: number };
  timestamp: number;
}

export interface ContainerSummary {
  id: string;
  names: string[];
  image: string;
  state: string;
  status: string;
  createdAt: number;
  ports: string[];
  mounts: string[];
  labels: Record<string, string>;
  /** Container IP on its first network, or null. */
  ip: string | null;
}

// ─── Proxy replicas (P-4 companion) ────────────────────────────────

export interface ProxyReplicaIdentity {
  containerId: string;
  containerName: string;
  replicaIndex: number | null;
  ip: string | null;
  state: string;
  startedAt: string | null;
}

export interface ProxyHttpRouteSummary {
  route: string;
  method: string;
  count: number;
  p95Seconds: number;
  errorRatio: number;
}

export interface ProxyReplicaMetrics {
  replicaId: string | null;
  hostname: string | null;
  process: {
    uptimeSeconds: number;
    residentMemoryBytes: number;
    heapUsedBytes: number;
    heapTotalBytes: number;
  };
  eventLoop: {
    p50Seconds: number;
    p99Seconds: number;
    maxSeconds: number;
  };
  dbPool: {
    total: number;
    idle: number;
    waiting: number;
  };
  http: {
    totalRequests: number;
    totalDurationSeconds: number;
    p50Seconds: number;
    p95Seconds: number;
    p99Seconds: number;
    errorRatio: number;
    topRoutes: ProxyHttpRouteSummary[];
  };
}

export interface ProxyReplicaSnapshot {
  identity: ProxyReplicaIdentity;
  scrapedAt: number;
  scrapeDurationMs: number;
  ok: boolean;
  error: string | null;
  metrics: ProxyReplicaMetrics | null;
}

export interface ProxyAggregateSnapshot {
  scrapedAt: number;
  replicas: ProxyReplicaSnapshot[];
  aggregate: {
    replicaCount: number;
    reachable: number;
    totalRequests: number;
    meanP95Seconds: number;
    maxP99Seconds: number;
    maxEventLoopP99Seconds: number;
    totalRssBytes: number;
    totalDbPoolWaiting: number;
    totalDbPoolTotal: number;
  } | null;
}

export interface ContainerStats {
  id: string;
  cpuPercent: number;
  memUsage: number;
  memLimit: number;
  memPercent: number;
  netRx: number;
  netTx: number;
  blockRead: number;
  blockWrite: number;
  pids: number;
}

export type RunpodBuildStatus = 'running' | 'success' | 'failed' | 'timeout' | 'canceled';
export type RunpodBuildPhase = 'queued' | 'cloning' | 'building' | 'pushing' | 'cleanup' | 'done';

export interface RunpodBranchSummary extends BranchSummary {
  dockerhubRepo: string | null;
  runpodTemplateId: string | null;
  runpodEndpointId: string | null;
}

export interface RunpodBranchesResponse {
  branches: RunpodBranchSummary[];
  protectedBranch: string;
}

export interface RunpodMapping {
  branch: string;
  dockerhubRepo: string;
  runpodTemplateId: string | null;
  runpodEndpointId: string | null;
  updatedAt: number;
}

export type RunpodModelLocation = 'image' | 'volume';

export interface RunpodModelEntry {
  filename: string;
  url: string;
  /** Human-readable name surfaced by blueeden-app dropdowns. */
  displayName?: string;
  /** Free-form description (range, compatibility, …) shown in the inventory tab. */
  description?: string;
  /** Comma-separated trigger words (LoRAs primarily). */
  triggerWords?: string;
  /** Where the file lives at runtime — image (default) or network volume. */
  location?: RunpodModelLocation;
}

export interface RunpodModelSection {
  folder: string;
  items: RunpodModelEntry[];
}

export interface RunpodModelsResponse {
  branch: string;
  baseSha: string;
  sections: RunpodModelSection[];
  emptyFolders: string[];
  notFound: boolean;
  cudaArchitectures: string | null;
}

export interface RunpodCustomNode {
  id: number;
  name: string;
  originalFilename: string;
  storedFilename: string;
  sizeBytes: number;
  sha256: string;
  description: string | null;
  uploadedBy: number;
  uploadedByUsername: string | null;
  uploadedAt: number;
  attachedBranches: string[];
}

/**
 * Privately-stored model file (LoRA, checkpoint, VAE, …) baked into a
 * branch's Docker image at build time via a COPY directive. Identified in
 * the model editor by `local://<name>`.
 */
export interface RunpodCustomModel {
  id: number;
  name: string;
  originalFilename: string;
  /** Default ComfyUI subfolder (loras / checkpoints / …), editable. */
  folder: string;
  storedFilename: string;
  sizeBytes: number;
  sha256: string;
  description: string | null;
  uploadedBy: number;
  uploadedByUsername: string | null;
  uploadedAt: number;
  attachedBranches: string[];
}

export interface RunpodNetworkVolume {
  id: string;
  name: string;
  size: number;
  dataCenterId: string;
}

export type RunpodUploadStatus =
  | 'running'
  | 'success'
  | 'failed'
  | 'timeout'
  | 'canceled';
export type RunpodUploadPhase =
  | 'queued'
  | 'preparing'
  | 'creating_pod'
  | 'waiting'
  | 'downloading'
  | 'verifying'
  | 'cleanup'
  | 'done';

export interface RunpodUploadRecord {
  id: number;
  userId: number;
  username: string;
  branch: string;
  commitSha: string | null;
  networkVolumeId: string;
  dataCenterId: string;
  podId: string | null;
  modelsTotal: number;
  modelsDone: number;
  modelsFailed: number;
  bytesDownloaded: number;
  status: RunpodUploadStatus;
  phase: RunpodUploadPhase | null;
  startedAt: number;
  finishedAt: number | null;
  logFile: string;
  errorMessage: string | null;
}

export interface RunpodBuildRecord {
  id: number;
  userId: number;
  username: string;
  branch: string;
  commitSha: string | null;
  dockerhubRepo: string;
  dockerhubTag: string;
  /** Unique tag pushed alongside dockerhubTag — `<mobile>-<sha7>-b<id>`. */
  immutableTag: string | null;
  status: RunpodBuildStatus;
  phase: RunpodBuildPhase | null;
  startedAt: number;
  finishedAt: number | null;
  exitCode: number | null;
  logFile: string;
  errorMessage: string | null;
  imageSize: number | null;
  imageRemoved: boolean;
}

export interface VolumeSummary {
  name: string;
  driver: string;
  mountpoint: string;
  createdAt: string | null;
  labels: Record<string, string>;
  scope: string;
  size: number;
  refCount: number;
}

export interface ImageSummary {
  id: string;
  repoTags: string[];
  repoDigests: string[];
  createdAt: number;
  size: number;
  labels: Record<string, string>;
  dangling: boolean;
  containers: number;
}

export type EnvFileEntry =
  | { type: 'kv'; key: string; value: string; masked?: boolean }
  | { type: 'comment'; text: string }
  | { type: 'blank' };

export interface EnvFileSnapshot {
  path: string;
  exists: boolean;
  entries: EnvFileEntry[];
}

// ─── Identity admin (blueeden-auth accounts) ──────────────────────

export interface IdentityOAuthAccount {
  provider: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  createdAt: number;
}

export interface IdentityAuditRecord {
  id: number;
  actor: string;
  action: string;
  details: unknown;
  createdAt: number;
}

export interface IdentityUserSummary {
  id: string;
  username: string;
  email: string | null;
  emailVerified: boolean;
  role: string;
  disabled: boolean;
  hasPassword: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface IdentityUserDetail extends IdentityUserSummary {
  emailVerifiedAt: number | null;
  pendingEmail: string | null;
  mustChangePassword: boolean;
  locale: string | null;
  sessionCutoff: number;
  disabledAt: number | null;
  disabledReason: string | null;
  oauthAccounts: IdentityOAuthAccount[];
  recentLoginFailures: number;
  audit: IdentityAuditRecord[];
}

export interface IdentityUsersResponse {
  users: IdentityUserSummary[];
  total: number;
  limit: number;
  offset: number;
}

/* ── Invitations ──────────────────────────────────────────────────────────────
   Mirrors `@blueeden/auth-shared/invitations`. This console cannot import that
   package (separate repo, no published artifact), so the shapes are restated —
   and only the fields this UI actually reads, so a drift shows up as a missing
   field here rather than as a silent mismatch. The IdP is the authority on all
   of it; nothing below re-implements a rule.
   ───────────────────────────────────────────────────────────────────────────── */

export type InvitationAppLevel = 'standard' | 'tester';

/** Where an invitation sends someone. Served by the IdP, never hard-coded here. */
export interface InvitationApp {
  id: string;
  label: string;
  url: string;
}

export type InvitationStatus = 'pending' | 'visited' | 'redeemed' | 'expired' | 'revoked';

export interface InvitationRecord {
  id: string;
  email: string;
  app: string;
  appLevel: InvitationAppLevel;
  status: InvitationStatus;
  createdAt: number;
  createdByLabel: string;
  expiresAt: number;
  visitedAt?: number;
  visitCount: number;
  redeemedAt?: number;
  redeemedUserId?: string;
  /** True when an EXISTING account was attached rather than a new one created. */
  redeemedExisting: boolean;
  revokedAt?: number;
  emailSentAt?: number;
  emailError?: string;
  message?: string;
}

/** An invitation plus its ONE-TIME url. Only ever present on mint/regenerate. */
export interface MintedInvitation extends InvitationRecord {
  url: string;
}

export interface CreateInvitationsBody {
  emails: string;
  app: string;
  appLevel: InvitationAppLevel;
  sendEmail?: boolean;
  message?: string;
  expiresInDays?: number;
}

export interface InvitationCreateOutcome {
  email: string;
  ok: boolean;
  invitation?: MintedInvitation;
  reason?: 'invalid-email' | 'duplicate-in-list' | 'already-invited' | 'already-redeemed';
  existing?: InvitationRecord;
}

export interface CreateInvitationsResult {
  results: InvitationCreateOutcome[];
  created: number;
}

export interface InvitationListResponse {
  invitations: InvitationRecord[];
  apps: InvitationApp[];
}

export interface IdentityUserUpdate {
  username?: string;
  email?: string | null;
  emailVerified?: boolean;
  role?: string;
  mustChangePassword?: boolean;
  locale?: string | null;
}

// ─── API keys (fine-grained machine credentials) ──────────────────

export type ApiKeyScope = 'deploy:start' | 'deploy:read' | 'build:start' | 'build:read';

export interface ApiKeySummary {
  id: number;
  name: string;
  tokenPrefix: string;
  createdBy: string;
  scopes: ApiKeyScope[];
  /** null = every application. */
  appIds: number[] | null;
  /** null = any branch. */
  branchPatterns: string[] | null;
  expiresAt: number | null;
  revokedAt: number | null;
  lastUsedAt: number | null;
  createdAt: number;
}

export interface ApiKeyCreateInput {
  name: string;
  scopes: ApiKeyScope[];
  appIds?: number[] | null;
  branchPatterns?: string[] | null;
  expiresAt?: number | null;
}

export interface ApiKeyCreateResult {
  key: ApiKeySummary;
  /** Cleartext token — only ever present in this response. */
  token: string;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private csrfToken: string | null = null;
  private currentAppId: number | null = null;

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  /** Selects the application all app-scoped calls below are issued against. */
  setCurrentApp(appId: number | null): void {
    this.currentAppId = appId;
  }

  /** Appends the current ?app=<id> to an app-scoped path. */
  private withApp(path: string): string {
    if (this.currentAppId == null) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}app=${this.currentAppId}`;
  }

  listApplications(): Promise<ApplicationsResponse> {
    return this.request('GET', '/api/applications');
  }

  createApplication(input: ApplicationUpsert): Promise<{ application: Application }> {
    return this.request('POST', '/api/applications', input);
  }

  updateApplication(id: number, input: ApplicationUpsert): Promise<{ application: Application }> {
    return this.request('PUT', `/api/applications/${id}`, input);
  }

  deleteApplication(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/applications/${id}`);
  }

  // ─── API keys ───────────────────────────────────────────────
  listApiKeys(): Promise<{ items: ApiKeySummary[] }> {
    return this.request('GET', '/api/api-keys');
  }

  createApiKey(input: ApiKeyCreateInput): Promise<ApiKeyCreateResult> {
    return this.request('POST', '/api/api-keys', input);
  }

  revokeApiKey(id: number): Promise<{ key: ApiKeySummary }> {
    return this.request('POST', `/api/api-keys/${id}/revoke`);
  }

  // ─── Identity admin (blueeden-auth accounts) ────────────────
  listIdentityUsers(opts: { q?: string | undefined; limit?: number | undefined; offset?: number | undefined } = {}): Promise<IdentityUsersResponse> {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return this.request('GET', `/api/identity/users${qs ? `?${qs}` : ''}`);
  }

  getIdentityUser(id: string): Promise<{ user: IdentityUserDetail }> {
    return this.request('GET', `/api/identity/users/${encodeURIComponent(id)}`);
  }

  updateIdentityUser(id: string, body: IdentityUserUpdate): Promise<{ user: IdentityUserDetail }> {
    return this.request('PUT', `/api/identity/users/${encodeURIComponent(id)}`, body);
  }

  setIdentityPassword(id: string, password: string, forceChange: boolean): Promise<{ ok: true }> {
    return this.request('POST', `/api/identity/users/${encodeURIComponent(id)}/password`, { password, forceChange });
  }

  sendIdentityReset(id: string): Promise<{ ok: true }> {
    return this.request('POST', `/api/identity/users/${encodeURIComponent(id)}/send-reset`);
  }

  revokeIdentitySessions(id: string): Promise<{ ok: true }> {
    return this.request('POST', `/api/identity/users/${encodeURIComponent(id)}/revoke-sessions`);
  }

  disableIdentityUser(id: string, reason: string): Promise<{ user: IdentityUserDetail }> {
    return this.request('POST', `/api/identity/users/${encodeURIComponent(id)}/disable`, { reason });
  }

  enableIdentityUser(id: string): Promise<{ user: IdentityUserDetail }> {
    return this.request('POST', `/api/identity/users/${encodeURIComponent(id)}/enable`);
  }

  // ── Invitations ──────────────────────────────────────────────────────────

  listInvitations(opts: { app?: string | undefined; email?: string | undefined; limit?: number | undefined; offset?: number | undefined } = {}): Promise<InvitationListResponse> {
    const params = new URLSearchParams();
    if (opts.app) params.set('app', opts.app);
    if (opts.email) params.set('email', opts.email);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return this.request('GET', `/api/identity/invitations${qs ? `?${qs}` : ''}`);
  }

  createInvitations(body: CreateInvitationsBody): Promise<CreateInvitationsResult> {
    return this.request('POST', '/api/identity/invitations', body);
  }

  revokeInvitation(id: string): Promise<{ invitation: InvitationRecord }> {
    return this.request('DELETE', `/api/identity/invitations/${encodeURIComponent(id)}`);
  }

  regenerateInvitation(id: string, body: { sendEmail?: boolean; message?: string } = {}): Promise<{ invitation: MintedInvitation }> {
    return this.request('POST', `/api/identity/invitations/${encodeURIComponent(id)}/regenerate`, body);
  }

  deleteIdentityUser(id: string): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/identity/users/${encodeURIComponent(id)}`);
  }

  async login(username: string, password: string): Promise<AuthMe> {
    const data = await this.request<AuthMe>('POST', '/api/auth/login', { username, password });
    this.csrfToken = data.csrfToken;
    return data;
  }

  async logout(): Promise<void> {
    await this.request<{ ok: true }>('POST', '/api/auth/logout');
    this.csrfToken = null;
  }

  async me(): Promise<AuthMe | null> {
    try {
      const data = await this.request<AuthMe>('GET', '/api/auth/me');
      this.csrfToken = data.csrfToken;
      return data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  }

  listBranches(): Promise<BranchesResponse> {
    return this.request('GET', this.withApp('/api/branches'));
  }

  /** Ahead/behind counters for every branch against `base` (default: protected branch). */
  compareBranches(base?: string): Promise<BranchComparisonResponse> {
    const path = base ? `/api/branches/compare?base=${encodeURIComponent(base)}` : '/api/branches/compare';
    return this.request('GET', this.withApp(path));
  }

  mergeBranch(name: string, message?: string): Promise<MergeResult> {
    const body = message ? { message } : {};
    return this.request('POST', this.withApp(`/api/branches/${encodeURIComponent(name)}/merge`), body);
  }

  deleteBranches(branches: string[]): Promise<DeleteBranchesResult> {
    return this.request('POST', this.withApp('/api/branches/delete'), { branches });
  }

  startDeploy(branch?: string): Promise<{ deployment: DeploymentRecord }> {
    return this.request('POST', this.withApp('/api/deploy'), branch ? { branch } : {});
  }

  getEnvFile(): Promise<EnvFileSnapshot> {
    return this.request('GET', this.withApp('/api/env-file'));
  }

  saveEnvFile(entries: EnvFileEntry[]): Promise<EnvFileSnapshot> {
    return this.request('PUT', this.withApp('/api/env-file'), { entries });
  }

  revealEnvValue(key: string): Promise<{ key: string; value: string }> {
    return this.request('GET', this.withApp(`/api/env-file/value?key=${encodeURIComponent(key)}`));
  }

  currentDeploy(): Promise<{ active: false } | { active: true; deployment: DeploymentRecord }> {
    return this.request('GET', this.withApp('/api/deploy/current'));
  }

  listLogs(limit = 30, offset = 0): Promise<{ items: DeploymentRecord[] }> {
    return this.request('GET', this.withApp(`/api/logs?limit=${limit}&offset=${offset}`));
  }

  getLog(id: number): Promise<{ deployment: DeploymentRecord; log: string }> {
    return this.request('GET', this.withApp(`/api/logs/${id}`));
  }

  deleteLog(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', this.withApp(`/api/logs/${id}`));
  }

  listContainers(): Promise<{ items: ContainerSummary[] }> {
    return this.request('GET', '/api/monitoring/containers');
  }

  listVolumes(): Promise<{ items: VolumeSummary[] }> {
    return this.request('GET', '/api/monitoring/volumes');
  }

  listImages(): Promise<{ items: ImageSummary[] }> {
    return this.request('GET', '/api/monitoring/images');
  }

  removeImage(
    ref: string,
    force = false,
  ): Promise<{ ok: true; items: { Untagged?: string; Deleted?: string }[] }> {
    const qs = force ? '?force=1' : '';
    return this.request('DELETE', `/api/monitoring/images/${encodeURIComponent(ref)}${qs}`);
  }

  pruneImages(all = false): Promise<{
    ok: true;
    deleted: { Untagged?: string; Deleted?: string }[];
    reclaimedBytes: number;
  }> {
    return this.request('POST', `/api/monitoring/images/prune${all ? '?all=1' : ''}`);
  }

  systemDf(): Promise<{
    layersSize: number;
    categories: {
      type: 'images' | 'containers' | 'volumes' | 'buildCache';
      active: number;
      reclaimable: number;
      total: number;
      inactive: number;
    }[];
  }> {
    return this.request('GET', '/api/monitoring/df');
  }

  pruneBuildCache(all = false): Promise<{
    ok: true;
    deleted: string[];
    reclaimedBytes: number;
  }> {
    return this.request('POST', `/api/monitoring/build-cache/prune${all ? '?all=1' : ''}`);
  }

  pruneContainers(): Promise<{
    ok: true;
    deleted: string[];
    reclaimedBytes: number;
  }> {
    return this.request('POST', '/api/monitoring/containers/prune');
  }

  // ─── Proxy replicas (companion to AUDIT_LOAD.md P-4) ─────────
  listProxyReplicas(): Promise<{ replicas: ProxyReplicaIdentity[] }> {
    return this.request('GET', this.withApp('/api/monitoring/proxy/replicas'));
  }

  getProxyMetrics(): Promise<ProxyAggregateSnapshot> {
    return this.request('GET', this.withApp('/api/monitoring/proxy/metrics'));
  }

  streamProxyMetrics(handlers: {
    onSnapshot?: (snap: ProxyAggregateSnapshot) => void;
    onError?: (ev: Event) => void;
  }): () => void {
    const es = new EventSource(this.withApp('/api/monitoring/proxy/metrics/stream'));
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as ProxyAggregateSnapshot;
      handlers.onSnapshot?.(data);
    });
    es.addEventListener('error', (ev) => handlers.onError?.(ev));
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  streamHostMetrics(handlers: {
    onSnapshot?: (m: HostMetrics) => void;
    onError?: (ev: Event) => void;
  }): () => void {
    const es = new EventSource('/api/monitoring/host/stream');
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as HostMetrics;
      handlers.onSnapshot?.(data);
    });
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  streamContainers(handlers: {
    onSnapshot?: (data: { containers: ContainerSummary[]; stats: ContainerStats[]; timestamp: number }) => void;
    onError?: (ev: Event) => void;
  }): () => void {
    const es = new EventSource('/api/monitoring/containers/stream');
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as {
        containers: ContainerSummary[];
        stats: ContainerStats[];
        timestamp: number;
      };
      handlers.onSnapshot?.(data);
    });
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  streamContainerLogs(
    id: string,
    handlers: {
      onLog?: (chunk: string) => void;
      onEnd?: (exitCode: number) => void;
      onError?: (ev: Event) => void;
    },
  ): () => void {
    const es = new EventSource(`/api/monitoring/containers/${encodeURIComponent(id)}/logs/stream`);
    es.addEventListener('log', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { chunk: string };
      handlers.onLog?.(data.chunk);
    });
    es.addEventListener('end', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { exitCode: number };
      handlers.onEnd?.(data.exitCode);
      es.close();
    });
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  volumeBackupUrl(name: string): string {
    return `/api/monitoring/volumes/${encodeURIComponent(name)}/backup`;
  }

  async restoreVolume(name: string, file: Blob): Promise<{ ok: true }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/gzip',
    };
    if (this.csrfToken) headers['X-CSRF-Token'] = this.csrfToken;
    const res = await fetch(
      `/api/monitoring/volumes/${encodeURIComponent(name)}/restore?confirm=yes`,
      {
        method: 'POST',
        headers,
        body: file,
        credentials: 'same-origin',
      },
    );
    const text = await res.text();
    let json: unknown = null;
    try { json = text ? JSON.parse(text) : null; } catch { /* ignore */ }
    if (!res.ok) {
      const message = json && typeof json === 'object' && 'error' in json
        ? String((json as { error: unknown }).error)
        : `HTTP ${res.status}`;
      throw new ApiError(res.status, message);
    }
    return json as { ok: true };
  }

  // ── Runpod images ──────────────────────────────────────
  listRunpodBranches(): Promise<RunpodBranchesResponse> {
    return this.request('GET', '/api/runpod-images/branches');
  }

  deleteRunpodBranches(branches: string[]): Promise<DeleteBranchesResult> {
    return this.request('POST', '/api/runpod-images/branches/delete', { branches });
  }

  listRunpodMappings(): Promise<{ mappings: RunpodMapping[] }> {
    return this.request('GET', '/api/runpod-images/mappings');
  }

  setRunpodMapping(
    branch: string,
    dockerhubRepo: string,
    runpodTemplateId?: string | null,
    runpodEndpointId?: string | null,
  ): Promise<{ mapping: RunpodMapping }> {
    const body: {
      dockerhubRepo: string;
      runpodTemplateId?: string;
      runpodEndpointId?: string;
    } = { dockerhubRepo };
    // Empty string clears the corresponding ID; omitted leaves untouched.
    if (runpodTemplateId !== undefined) body.runpodTemplateId = runpodTemplateId ?? '';
    if (runpodEndpointId !== undefined) body.runpodEndpointId = runpodEndpointId ?? '';
    return this.request(
      'PUT',
      `/api/runpod-images/mappings/${encodeURIComponent(branch)}`,
      body,
    );
  }

  deleteRunpodMapping(branch: string): Promise<{ ok: true; removed: boolean }> {
    return this.request(
      'DELETE',
      `/api/runpod-images/mappings/${encodeURIComponent(branch)}`,
    );
  }

  getRunpodModels(branch: string): Promise<RunpodModelsResponse> {
    return this.request('GET', `/api/runpod-images/branches/${encodeURIComponent(branch)}/models`);
  }

  saveRunpodModels(
    branch: string,
    payload: {
      baseSha: string;
      sections: RunpodModelSection[];
      cudaArchitectures?: string;
    },
  ): Promise<{
    ok: true;
    unchanged: boolean;
    baseSha: string;
    commitSha?: string;
    message?: string;
    diff?: { added: number; removed: number; changed: number };
  }> {
    return this.request(
      'PUT',
      `/api/runpod-images/branches/${encodeURIComponent(branch)}/models`,
      payload,
    );
  }

  // ── Runpod custom nodes (.zip uploads) ───────────────────
  listRunpodCustomNodes(): Promise<{ items: RunpodCustomNode[] }> {
    return this.request('GET', '/api/runpod-custom-nodes');
  }

  getRunpodCustomNode(id: number): Promise<{ item: RunpodCustomNode }> {
    return this.request('GET', `/api/runpod-custom-nodes/${id}`);
  }

  /**
   * Streams a .zip file (read from a File/Blob) into the server. The
   * server-side route reads the body directly — no multipart/form-data
   * overhead. Returns the freshly-created custom node record.
   */
  async uploadRunpodCustomNode(input: {
    name: string;
    file: File | Blob;
    filename?: string;
    description?: string;
  }): Promise<{ item: RunpodCustomNode }> {
    const params = new URLSearchParams({ name: input.name });
    const fname = input.filename ?? (input.file instanceof File ? input.file.name : '');
    if (fname) params.set('filename', fname);
    if (input.description) params.set('description', input.description);
    const headers: Record<string, string> = { 'Content-Type': 'application/zip' };
    if (this.csrfToken) headers['X-CSRF-Token'] = this.csrfToken;
    const res = await fetch(`/api/runpod-custom-nodes?${params.toString()}`, {
      method: 'POST',
      headers,
      body: input.file,
      credentials: 'same-origin',
    });
    const text = await res.text();
    let json: unknown = null;
    try { json = text ? JSON.parse(text) : null; } catch { /* ignore */ }
    if (!res.ok) {
      const message = json && typeof json === 'object' && 'error' in json
        ? String((json as { error: unknown }).error)
        : `HTTP ${res.status}`;
      const code = json && typeof json === 'object' && 'code' in json
        ? String((json as { code: unknown }).code)
        : undefined;
      throw new ApiError(res.status, message, code);
    }
    return json as { item: RunpodCustomNode };
  }

  updateRunpodCustomNode(
    id: number,
    payload: { description?: string },
  ): Promise<{ item: RunpodCustomNode }> {
    return this.request('PATCH', `/api/runpod-custom-nodes/${id}`, payload);
  }

  deleteRunpodCustomNode(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/runpod-custom-nodes/${id}`);
  }

  runpodCustomNodeDownloadUrl(id: number): string {
    return `/api/runpod-custom-nodes/${id}/download`;
  }

  // ── Runpod custom models (private LoRAs / checkpoints / …) ─
  listRunpodCustomModels(): Promise<{ items: RunpodCustomModel[] }> {
    return this.request('GET', '/api/runpod-custom-models');
  }

  /**
   * Uploads a binary file (any format — checkpoints, LoRAs, VAEs, …) using
   * the chunked init/append/finalize protocol so multi-GB files survive any
   * proxy / OS / kernel timeout that would kill a single long-lived POST.
   *
   * The `name` slug is the unique identifier used as `local://<name>` in
   * the Edit Models modal; `folder` becomes the default ComfyUI subfolder
   * (editable later via PATCH).
   *
   * onProgress fires after each chunk completes (granularity = chunk size).
   * Per-chunk transient network errors are retried up to MAX_RETRIES times
   * with exponential backoff before the upload is aborted.
   */
  async uploadRunpodCustomModel(input: {
    name: string;
    folder: string;
    file: File | Blob;
    filename?: string;
    description?: string;
    onProgress?: (loaded: number, total: number) => void;
  }): Promise<{ item: RunpodCustomModel }> {
    const total = input.file.size;
    const fname = input.filename ?? (input.file instanceof File ? input.file.name : '');

    // Step 1 — init the session, get an uploadId and the server's
    // suggested chunk size.
    const init = await this.request<{ uploadId: string; chunkSize: number }>(
      'POST',
      '/api/runpod-custom-models/uploads',
      {},
    );
    const uploadId = init.uploadId;
    // Clamp chunk size to a sane range in case the server hint is silly.
    const chunkSize = Math.max(
      1024 * 1024,
      Math.min(init.chunkSize || 25 * 1024 * 1024, 100 * 1024 * 1024),
    );

    const MAX_RETRIES = 4;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    try {
      // Step 2 — push chunks sequentially.
      let offset = 0;
      while (offset < total) {
        let attempt = 0;
        while (true) {
          const end = Math.min(offset + chunkSize, total);
          const slice = input.file.slice(offset, end);
          try {
            await this.uploadOneChunk(uploadId, offset, slice);
            offset = end;
            input.onProgress?.(offset, total);
            break;
          } catch (err) {
            const status = err instanceof ApiError ? err.status : 0;
            // 0 = network blip, 5xx = server hiccup, 408 = request timeout,
            // 409 = offset_mismatch / session_busy (resync below recovers).
            const retryable =
              status === 0 || status >= 500 || status === 408 || status === 409;
            if (!retryable || attempt >= MAX_RETRIES) throw err;
            // Resync: the previous chunk may actually have been received
            // server-side and only the response got lost — in that case
            // the server's receivedBytes is ahead of our offset.
            try {
              const view = await this.request<{ receivedBytes: number }>(
                'GET',
                `/api/runpod-custom-models/uploads/${uploadId}`,
              );
              if (view.receivedBytes > offset) {
                offset = Math.min(view.receivedBytes, total);
                input.onProgress?.(offset, total);
                if (offset >= total) break;
                attempt = 0;
                continue;
              }
            } catch { /* ignore — fall through to backoff */ }
            const backoffMs = 1000 * 2 ** attempt;
            await sleep(backoffMs);
            attempt++;
          }
        }
      }

      // Empty file — the server rejects empty uploads at finalize time, so
      // we don't pre-empt that here.

      // Step 3 — finalize: server validates metadata, hashes, creates DB row.
      return await this.request<{ item: RunpodCustomModel }>(
        'POST',
        `/api/runpod-custom-models/uploads/${uploadId}/finalize`,
        {
          name: input.name,
          folder: input.folder,
          filename: fname || undefined,
          description: input.description || undefined,
        },
      );
    } catch (err) {
      // Best-effort cleanup of the server-side session + .part file. Don't
      // shadow the original error if cleanup itself fails.
      try {
        await this.request('DELETE', `/api/runpod-custom-models/uploads/${uploadId}`);
      } catch { /* ignore */ }
      throw err;
    }
  }

  /**
   * Sends a single chunk via XMLHttpRequest so the call surfaces granular
   * upload progress for the chunk currently in flight (fetch's body
   * progress is still not portable across browsers).
   */
  private uploadOneChunk(uploadId: string, offset: number, blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(
        'POST',
        `/api/runpod-custom-models/uploads/${uploadId}/chunks?offset=${offset}`,
        true,
      );
      xhr.responseType = 'text';
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      if (this.csrfToken) xhr.setRequestHeader('X-CSRF-Token', this.csrfToken);
      xhr.addEventListener('load', () => {
        const text = xhr.responseText ?? '';
        let json: unknown = null;
        try { json = text ? JSON.parse(text) : null; } catch { /* ignore */ }
        if (xhr.status < 200 || xhr.status >= 300) {
          const message = json && typeof json === 'object' && 'error' in json
            ? String((json as { error: unknown }).error)
            : `HTTP ${xhr.status}`;
          const code = json && typeof json === 'object' && 'code' in json
            ? String((json as { code: unknown }).code)
            : undefined;
          reject(new ApiError(xhr.status, message, code));
          return;
        }
        resolve();
      });
      xhr.addEventListener('error', () => reject(new ApiError(0, 'network error')));
      xhr.addEventListener('abort', () => reject(new ApiError(0, 'aborted')));
      xhr.send(blob);
    });
  }

  updateRunpodCustomModel(
    id: number,
    payload: { folder?: string; description?: string },
  ): Promise<{ item: RunpodCustomModel }> {
    return this.request('PATCH', `/api/runpod-custom-models/${id}`, payload);
  }

  deleteRunpodCustomModel(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/runpod-custom-models/${id}`);
  }

  runpodCustomModelDownloadUrl(id: number): string {
    return `/api/runpod-custom-models/${id}/download`;
  }

  // ── Runpod custom nodes attached per branch ──────────────
  listBranchCustomNodes(branch: string): Promise<{ branch: string; items: RunpodCustomNode[] }> {
    return this.request(
      'GET',
      `/api/runpod-images/branches/${encodeURIComponent(branch)}/custom-nodes`,
    );
  }

  setBranchCustomNodes(
    branch: string,
    customNodeIds: number[],
  ): Promise<{ branch: string; items: RunpodCustomNode[] }> {
    return this.request(
      'PUT',
      `/api/runpod-images/branches/${encodeURIComponent(branch)}/custom-nodes`,
      { customNodeIds },
    );
  }

  attachBranchCustomNode(
    branch: string,
    id: number,
  ): Promise<{ branch: string; items: RunpodCustomNode[] }> {
    return this.request(
      'POST',
      `/api/runpod-images/branches/${encodeURIComponent(branch)}/custom-nodes/${id}`,
    );
  }

  detachBranchCustomNode(
    branch: string,
    id: number,
  ): Promise<{ branch: string; items: RunpodCustomNode[] }> {
    return this.request(
      'DELETE',
      `/api/runpod-images/branches/${encodeURIComponent(branch)}/custom-nodes/${id}`,
    );
  }

  // ── Runpod network volume uploader ───────────────────────
  listRunpodNetworkVolumes(): Promise<{ volumes: RunpodNetworkVolume[] }> {
    return this.request('GET', '/api/runpod-images/network-volumes');
  }

  /**
   * Start a sync of the branch's volume manifest against ONE OR MORE
   * network volumes. Each picked volume gets its own upload pod that
   * downloads missing files AND removes any model on the volume that's
   * not in the manifest. Returns one record per volume.
   */
  startRunpodVolumeUpload(
    branch: string,
    networkVolumeIds: readonly string[],
  ): Promise<{ uploads: RunpodUploadRecord[]; upload: RunpodUploadRecord }> {
    return this.request('POST', '/api/runpod-images/uploads', {
      branch,
      networkVolumeIds,
    });
  }

  listRunpodVolumeUploads(opts: { limit?: number; offset?: number; branch?: string } = {}): Promise<{
    items: RunpodUploadRecord[];
    active: number[];
  }> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.branch !== undefined) params.set('branch', opts.branch);
    const qs = params.toString();
    return this.request('GET', `/api/runpod-images/uploads${qs ? `?${qs}` : ''}`);
  }

  cancelRunpodVolumeUpload(id: number): Promise<{ ok: true }> {
    return this.request('POST', `/api/runpod-images/uploads/${id}/cancel`);
  }

  deleteRunpodVolumeUpload(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/runpod-images/uploads/${id}`);
  }

  deleteRunpodVolumeUploads(ids: number[]): Promise<{
    results: { id: number; ok: boolean; reason?: string }[];
  }> {
    return this.request('POST', '/api/runpod-images/uploads/delete', { ids });
  }

  streamRunpodVolumeUpload(
    id: number,
    handlers: {
      onSnapshot?: (upload: RunpodUploadRecord) => void;
      onLog?: (chunk: string) => void;
      onPhase?: (phase: RunpodUploadPhase) => void;
      onProgress?: (info: {
        modelsDone: number;
        modelsFailed: number;
        bytesDownloaded: number;
      }) => void;
      onFinish?: (status: RunpodUploadStatus) => void;
      onError?: (err: Event) => void;
    },
  ): () => void {
    const es = new EventSource(`/api/runpod-images/uploads/${id}/stream`);
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { upload: RunpodUploadRecord };
      handlers.onSnapshot?.(data.upload);
    });
    es.addEventListener('log', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { chunk: string };
      handlers.onLog?.(data.chunk);
    });
    es.addEventListener('phase', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { phase: RunpodUploadPhase };
      handlers.onPhase?.(data.phase);
    });
    es.addEventListener('progress', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as {
        modelsDone: number;
        modelsFailed: number;
        bytesDownloaded: number;
      };
      handlers.onProgress?.(data);
    });
    es.addEventListener('finish', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { status: RunpodUploadStatus };
      handlers.onFinish?.(data.status);
      es.close();
    });
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  startRunpodBuild(branch: string, dockerhubRepo?: string): Promise<{ build: RunpodBuildRecord }> {
    return this.request(
      'POST',
      '/api/runpod-images/builds',
      dockerhubRepo ? { branch, dockerhubRepo } : { branch },
    );
  }

  listRunpodBuilds(opts: { limit?: number; offset?: number; branch?: string } = {}): Promise<{
    items: RunpodBuildRecord[];
    active: number[];
  }> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.branch !== undefined) params.set('branch', opts.branch);
    const qs = params.toString();
    return this.request('GET', `/api/runpod-images/builds${qs ? `?${qs}` : ''}`);
  }

  getRunpodBuild(id: number): Promise<{ build: RunpodBuildRecord; log: string }> {
    return this.request('GET', `/api/runpod-images/builds/${id}`);
  }

  cancelRunpodBuild(id: number): Promise<{ ok: true }> {
    return this.request('POST', `/api/runpod-images/builds/${id}/cancel`);
  }

  deleteRunpodBuild(id: number): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/runpod-images/builds/${id}`);
  }

  deleteRunpodBuilds(ids: number[]): Promise<{
    results: { id: number; ok: boolean; reason?: string }[];
  }> {
    return this.request('POST', '/api/runpod-images/builds/delete', { ids });
  }

  removeRunpodBuildImage(
    id: number,
    force = false,
  ): Promise<{ ok: boolean; output: string; build: RunpodBuildRecord | null }> {
    const qs = force ? '?force=1' : '';
    return this.request('DELETE', `/api/runpod-images/builds/${id}/image${qs}`);
  }

  streamRunpodBuild(
    id: number,
    handlers: {
      onSnapshot?: (build: RunpodBuildRecord) => void;
      onLog?: (chunk: string) => void;
      onPhase?: (phase: RunpodBuildPhase) => void;
      onFinish?: (status: RunpodBuildStatus) => void;
      onError?: (err: Event) => void;
    },
  ): () => void {
    const es = new EventSource(`/api/runpod-images/builds/${id}/stream`);
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { build: RunpodBuildRecord };
      handlers.onSnapshot?.(data.build);
    });
    es.addEventListener('log', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { chunk: string };
      handlers.onLog?.(data.chunk);
    });
    es.addEventListener('phase', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { phase: RunpodBuildPhase };
      handlers.onPhase?.(data.phase);
    });
    es.addEventListener('finish', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { status: RunpodBuildStatus };
      handlers.onFinish?.(data.status);
      es.close();
    });
    es.onerror = (ev) => handlers.onError?.(ev);
    return () => es.close();
  }

  streamDeploy(
    id: number,
    handlers: {
      onSnapshot?: (deployment: DeploymentRecord) => void;
      onLog?: (chunk: string) => void;
      onFinish?: (status: string) => void;
      onError?: (err: Event) => void;
    },
  ): () => void {
    const es = new EventSource(`/api/deploy/${id}/stream`);
    es.addEventListener('snapshot', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { deployment: DeploymentRecord };
      handlers.onSnapshot?.(data.deployment);
    });
    es.addEventListener('log', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { chunk: string };
      handlers.onLog?.(data.chunk);
    });
    es.addEventListener('finish', (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { status: string };
      handlers.onFinish?.(data.status);
      es.close();
    });
    es.onerror = (ev) => {
      handlers.onError?.(ev);
    };
    return () => es.close();
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.csrfToken && method !== 'GET' && method !== 'HEAD') {
      headers['X-CSRF-Token'] = this.csrfToken;
    }
    const init: RequestInit = {
      method,
      headers,
      credentials: 'same-origin',
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetch(path, init);
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      const message =
        json && typeof json === 'object' && 'error' in json
          ? String((json as { error: unknown }).error)
          : `HTTP ${res.status}`;
      const code =
        json && typeof json === 'object' && 'code' in json
          ? String((json as { code: unknown }).code)
          : undefined;
      throw new ApiError(res.status, message, code);
    }
    return json as T;
  }
}
